import React from 'react';
import data from '../data.json';

export default function HowItWorks() {
  const steps = data.howItWorks;

  return (
    <section id="how-it-works" style={{
      padding: '110px 0', background: 'var(--bg-dark)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Top accent line — Alpona colour spectrum */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: 'linear-gradient(90deg, transparent, var(--accent), var(--accent3), var(--accent2), transparent)',
      }} />

      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 78 }}>
          <div className="section-label center">Our Process</div>
          <h2 className="section-title">
            How Vylinx Turns Strategy Into Scalable Growth
          </h2>
          <p className="section-sub" style={{ margin: '0 auto', maxWidth: 560 }}>
            Kolkata's creative intellect meets global marketing precision — from first insight to compounding results.
          </p>
        </div>

        {/* Steps */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
          gap: 22, position: 'relative',
        }} className="steps-grid">
          {/* Connector line */}
          <div style={{
            position: 'absolute', top: 35, left: '12.5%', right: '12.5%',
            height: 1,
            background: 'linear-gradient(90deg, transparent, var(--accent), var(--accent2), transparent)',
            zIndex: 0,
          }} className="connector" />

          {steps.map((step, i) => (
            <div key={i} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', padding: '28px 20px',
              textAlign: 'center', position: 'relative', zIndex: 1,
              transition: 'all var(--ease)',
            }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(255,107,43,0.3)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div style={{
                width: 70, height: 70, borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255,107,43,0.15), rgba(0,194,168,0.12))',
                border: '1px solid rgba(255,107,43,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 18px', fontSize: 27,
              }}>
                {step.icon}
              </div>
              <div style={{
                fontSize: 10, fontWeight: 800, letterSpacing: 3,
                color: 'var(--accent)', marginBottom: 6, textTransform: 'uppercase',
              }}>
                {step.step}
              </div>
              <div style={{ fontSize: '1.08rem', fontWeight: 800, color: '#fff', marginBottom: 9 }}>
                {step.title}
              </div>
              <p style={{ fontSize: 13, color: 'var(--gray-400)', lineHeight: 1.7 }}>
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Feature block */}
        <div style={{
          marginTop: 76, display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 52, background: 'var(--bg-card2)', borderRadius: 18,
          padding: 48, border: '1px solid var(--border)',
          position: 'relative', overflow: 'hidden',
        }} className="feature-grid">
          {/* Top gradient line */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 2,
            background: 'linear-gradient(90deg, var(--accent), var(--accent3), var(--accent2))',
          }} />

          {/* Visual */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255,107,43,0.1), rgba(0,194,168,0.07))',
            borderRadius: 13, minHeight: 270, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,107,43,0.15)', textAlign: 'center', padding: 32,
          }}>
            <div>
              <span style={{ fontSize: 12, color: 'var(--gray-500)', display: 'block', marginBottom: 8, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700 }}>
                Our Growth Promise
              </span>
              <span style={{
                fontSize: '4.5rem', fontWeight: 900, letterSpacing: '-0.04em',
                background: 'linear-gradient(135deg, var(--accent), var(--accent3))',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                display: 'block', lineHeight: 1,
              }}>
                Full<br/>Focus
              </span>
              <span style={{ fontSize: 13, color: 'var(--gray-500)', display: 'block', marginTop: 12 }}>
                on your brand's success
              </span>
              <div style={{ marginTop: 26, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                {['ROI Focused', 'Transparent', 'AI-Assisted'].map(tag => (
                  <span key={tag} style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 800,
                    letterSpacing: 0.5, color: 'var(--accent2)',
                    background: 'rgba(0,194,168,0.12)', border: '1px solid rgba(0,194,168,0.25)',
                  }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 style={{
              fontSize: '1.55rem', fontWeight: 900, color: '#fff',
              marginBottom: 11, letterSpacing: '-0.025em',
            }}>
              We Build Intelligent Growth Engines Tailored to Your Business
            </h3>
            <p style={{ color: 'var(--gray-400)', fontSize: 14, lineHeight: 1.8, marginBottom: 30 }}>
              As a new agency, we bring something established agencies often lack — hunger, attention and a genuine stake in proving ourselves through your results.
            </p>
            {[
              { icon: '🧠', title: 'Deep Business Intelligence', desc: 'We study your audience, competitors and market signals to uncover real growth opportunities.' },
              { icon: '🎯', title: 'Tailored Growth Blueprint', desc: 'A custom multi-channel strategy combining AI, creativity and automation — aligned to your goals.' },
              { icon: '🚀', title: 'Execution That Drives Results', desc: 'AI-assisted execution with continuous optimisation — because results are the only metric that matters.' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 22 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 9, flexShrink: 0,
                  background: 'linear-gradient(135deg, rgba(255,107,43,0.15), rgba(0,194,168,0.1))',
                  border: '1px solid rgba(255,107,43,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
                }}>
                  {item.icon}
                </div>
                <div>
                  <div style={{ fontWeight: 800, color: '#fff', fontSize: 14, marginBottom: 3 }}>{item.title}</div>
                  <div style={{ color: 'var(--gray-400)', fontSize: 13, lineHeight: 1.65 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .steps-grid { grid-template-columns: repeat(2,1fr) !important; }
          .connector { display: none !important; }
          .feature-grid { grid-template-columns: 1fr !important; padding: 30px !important; }
        }
        @media (max-width: 560px) {
          .steps-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
